// Terms of use page — plain legal page, no video hero.
import type { ReactNode } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

interface LegalSectionProps {
  title: string;
  children: ReactNode;
}

function LegalSection({ title, children }: LegalSectionProps) {
  return (
    <section className="mt-12">
      <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight">{title}</h2>
      <div className="text-white/70 text-base leading-relaxed mt-4 space-y-4">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="bg-black min-h-screen">
      <Nav route="inner" />
      <main className="max-w-3xl mx-auto px-6 pt-40 md:pt-48 pb-24">
        <div className="text-white/40 text-sm tracking-widest uppercase">Legal</div>
        <h1 className="font-instrument text-5xl md:text-6xl text-white tracking-tight mt-4 leading-[1.05]">
          Terms of <em className="italic-serif text-white/85">use</em>
        </h1>
        <p className="text-white/60 text-base leading-relaxed mt-6">
          Last updated: 12 June 2026. These terms govern your use of paisums.com. By using the
          site you accept them.
        </p>

        <LegalSection title="Use of this site">
          <p>
            The content on this site is provided for general information about Paisums Technology
            and our services. It is not professional, legal or compliance advice. Engagement
            terms, deliverables and warranties for our services are set out in the written
            agreement we sign with each client — nothing on this site forms part of a contract.
          </p>
        </LegalSection>

        <LegalSection title="Intellectual property">
          <p>
            All content on this site — text, design, video, graphics and code — is owned by or
            licensed to Paisums Technology. You may view and share links to it; you may not
            reproduce it commercially without our written permission.
          </p>
        </LegalSection>

        <LegalSection title="Accuracy and availability">
          <p>
            We work to keep the site accurate and available, but provide it "as is" without
            warranties of any kind. Case study figures labelled as samples illustrate the class
            of outcome our systems target and are not representations about any specific client
            engagement.
          </p>
        </LegalSection>

        <LegalSection title="Liability">
          <p>
            To the maximum extent permitted by law, including the Australian Consumer Law and the
            New Zealand Consumer Guarantees Act where applicable, Paisums Technology is not liable
            for loss arising from your use of, or reliance on, this website.
          </p>
        </LegalSection>

        <LegalSection title="Third-party services">
          <p>
            The site uses Google Analytics and Cloudflare Turnstile, and links to third-party
            sites. Your use of those services is governed by their own terms and privacy
            policies. See our{" "}
            <a
              href="/privacy.html"
              className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors"
            >
              privacy policy
            </a>{" "}
            for how we handle personal information.
          </p>
        </LegalSection>

        <LegalSection title="Governing law and contact">
          <p>
            These terms are governed by the laws of New Zealand. Questions:{" "}
            <a
              href="mailto:info@paisums.com"
              className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors"
            >
              info@paisums.com
            </a>
            .
          </p>
        </LegalSection>
      </main>
      <Footer />
    </div>
  );
}
